import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router";
import { AuthProvider, SessionProvider, useSession } from "./services/auth";
import RootEntry from "./features/landing/root-entry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { OnboardingLayout } from "./features/onboarding/layout";
import { GoogleMapsApiProvider } from "./services/maps/google-maps-api-provider";
import { ProtectedLayout } from "./features/protected/layout";
import ProfileScreen from "./features/profile/profile.screen";
import BusinessAccountScreen from "./features/profile/business-account.screen";
import AddressScreen from "./features/profile/address.screen";
import InvoicesScreen from "./features/invoices/invoices.screen";
import OnboardingStep1Screen from "./features/onboarding/onboarding-step-1.screen";
import OnboardingSuccessScreen from "./features/onboarding/onboarding-successful";
import OrdersScreen from "./features/orders/orders.screen";
import OrderDetailsScreen from "./features/orders/order-details.screen";
import { CreateOrderLayout } from "./features/orders/create/create-order-layout";
import CreateShipmentDetailsScreen from "./features/orders/create/details.screen";
import CreateShipmentPricingScreen from "./features/orders/create/pricing.screen";
import CreateShipmentPaymentScreen from "./features/orders/create/payment.screen";
import CreateShipmentConfirmationScreen from "./features/orders/create/confirmation.screen";
import BulkShipScreen from "./features/orders/bulk-ship/bulk-ship.screen";
import BillingScreen from "./features/profile/billing.screen";

import { TrackingLayout } from "./features/tracking/layout";
import TrackingScreen from "./features/tracking/tracking.screen";
import PaymentMethodsScreen from "./features/profile/payment-method.screen";
import DynamicTitle from "./components/dynamic-title";
import SettingsScreen from "./features/profile/settings.screen";
import PackagingScreen from "./features/profile/packaging.screen";
import VouchersScreen from "./features/vouchers/vouchers.screen";
import CarriersScreen from "./features/carriers/carriers.screen";
import { ErrorLayout } from "./features/errors/layout";
import UnauthorizedScreen from "./features/errors/unauthorized.screen";
import NotFoundScreen from "./features/errors/not-found.screen";
import { Toaster } from "@/components/ui/toaster";
import SignInScreen from "./features/auth/sign-in.screen";
import SignUpScreen from "./features/auth/sign-up.screen";
import AuthRedirectScreen from "./features/auth/auth-redirect.screen";
import PreLinkVerificationScreen from "./features/auth/pre-link-verification.screen";
import { AUTH_PROVIDER } from "./services/auth/config";

const queryClient = new QueryClient();

const TestAccountRedirect = ({ children }: { children: React.ReactNode }) => {
  const { customer } = useSession();
  if (customer?.isTestAccount) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

/**
 * Wrapper that shows pre-link verification screen when needed
 * This handles the case where a user signs up with Clerk but their email
 * matches an existing customer account
 */
const PreLinkVerificationGuard = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { emailVerificationRequired, isLoading } = useSession();

  // Don't show verification screen while loading
  if (isLoading) {
    return null;
  }

  // Show verification screen if email verification is required
  if (emailVerificationRequired) {
    return <PreLinkVerificationScreen />;
  }

  return <>{children}</>;
};

export const Router = () => {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools buttonPosition="bottom-left" />
        <Toaster />
        <AuthProvider>
          <SessionProvider>
            <PreLinkVerificationGuard>
              <SocketProvider>
                <DynamicTitle />
                <Routes>
                  {/* Marketing pages live in the standalone marketing app.
                        `/` resolves auth, else sends anon visitors there. */}
                  <Route path="/" element={<RootEntry />} />
                  <Route element={<TrackingLayout />}>
                    <Route
                      path="/tracking/:trackingNumber"
                      element={<TrackingScreen />}
                    />
                    <Route
                      path="/track/:trackingNumber"
                      element={<TrackingScreen />}
                    />
                  </Route>

                  {/* Auth entry points — URL-addressable so the standalone
                        marketing site can deep-link to them. Clerk has hosted
                        pages; Auth0 kicks off loginWithRedirect on mount. */}
                  {AUTH_PROVIDER === "clerk" ? (
                    <>
                      <Route path="/sign-in/*" element={<SignInScreen />} />
                      <Route path="/sign-up/*" element={<SignUpScreen />} />
                    </>
                  ) : (
                    <>
                      <Route
                        path="/sign-in/*"
                        element={<AuthRedirectScreen />}
                      />
                      <Route
                        path="/sign-up/*"
                        element={<AuthRedirectScreen screenHint="signup" />}
                      />
                    </>
                  )}

                  <Route
                    element={
                      <GoogleMapsApiProvider>
                        <Outlet />
                      </GoogleMapsApiProvider>
                    }
                  >
                    {/* Onboarding */}
                    <Route element={<OnboardingLayout />}>
                      <Route
                        path="/onboarding/step-1"
                        element={<OnboardingStep1Screen />}
                      />
                      <Route
                        path="/onboarding/success"
                        element={<OnboardingSuccessScreen />}
                      />
                    </Route>

                    {/* Protected routes - all use same layout */}
                    <Route element={<ProtectedLayout />}>
                      {/* Shipments (main dashboard) */}
                      <Route path="/dashboard" element={<OrdersScreen />} />
                      <Route path="/orders" element={<OrdersScreen />} />
                      <Route
                        path="/orders/:orderId"
                        element={<OrderDetailsScreen />}
                      />

                      {/* New shipment — 4-step full-page flow */}
                      <Route
                        path="/new-shipment"
                        element={<CreateOrderLayout />}
                      >
                        <Route
                          index
                          element={
                            <Navigate to="/new-shipment/details" replace />
                          }
                        />
                        <Route
                          path="details"
                          element={<CreateShipmentDetailsScreen />}
                        />
                        <Route
                          path="pricing"
                          element={<CreateShipmentPricingScreen />}
                        />
                        <Route
                          path="payment"
                          element={<CreateShipmentPaymentScreen />}
                        />
                        <Route
                          path="confirmation"
                          element={<CreateShipmentConfirmationScreen />}
                        />
                      </Route>

                      <Route path="/bulk-ship" element={<BulkShipScreen />} />

                      {/* History */}
                      <Route
                        path="/history"
                        element={
                          <TestAccountRedirect>
                            <InvoicesScreen />
                          </TestAccountRedirect>
                        }
                      />
                      <Route
                        path="/invoices"
                        element={
                          <TestAccountRedirect>
                            <InvoicesScreen />
                          </TestAccountRedirect>
                        }
                      />

                      {/* Address Book */}
                      <Route
                        path="/my-address-book"
                        element={<AddressScreen />}
                      />

                      {/* Packaging */}
                      <Route
                        path="/my-packaging"
                        element={<PackagingScreen />}
                      />

                      {/* Carriers */}
                      <Route path="/carriers" element={<CarriersScreen />} />

                      {/* Billing & Payment */}
                      <Route
                        path="/billing"
                        element={
                          <TestAccountRedirect>
                            <BillingScreen />
                          </TestAccountRedirect>
                        }
                      />
                      <Route
                        path="/payment-methods"
                        element={
                          <TestAccountRedirect>
                            <PaymentMethodsScreen />
                          </TestAccountRedirect>
                        }
                      />

                      {/* Profile & Settings (from user dropdown) */}
                      <Route path="/account" element={<ProfileScreen />} />
                      <Route path="/vouchers" element={<VouchersScreen />} />
                      <Route path="/settings" element={<SettingsScreen />} />
                      <Route
                        path="/business-account"
                        element={<BusinessAccountScreen />}
                      />
                    </Route>
                  </Route>

                  {/* Error routes */}
                  <Route element={<ErrorLayout />}>
                    <Route
                      path="/unauthorized"
                      element={<UnauthorizedScreen />}
                    />
                    <Route path="*" element={<NotFoundScreen />} />
                  </Route>
                </Routes>
              </SocketProvider>
            </PreLinkVerificationGuard>
          </SessionProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
};
